//@flow
import * as React from 'react';
import { themr } from 'react-css-themr';
import defaultTheme from './BulkGeocodeEditView.scss';
import OverlayDialog from 'common/components/core/dialog/OverlayDialog';
import BaseGeocoderMap from 'common/components/core/map/BaseGeocoderMap';
import TabbedBulkAddressList from 'common/entity/locationMaster/TabbedBulkAddressList';
import { Loader } from 'common/components/core/loader/Loader';
import { GeocodeEditLocation } from 'common/models/internal/GeocodeEditLocation';
import { GroupedGeocodeEditLocation } from 'common/models/internal/GroupedGeocodeEditLocation';
import type { List } from 'immutable';

type Props = {
    theme: Object,
    locations: List<GeocodeEditLocation>,
    groupedLocations: List<GroupedGeocodeEditLocation>,
    allLocations: List<GeocodeEditLocation>,
    expandedLocationId: string,
    selectedLocationId: string,
    positionInSortedList?: number,
    groupPositionInList?: number,
    searchText: string,
    currentTab: string,
    groupBy: ?string,
    expandedGroupId: ?string,
    isBulkGeocodeInProgress: boolean,
    isValidateGeocodesActive: boolean,
    isValidatingGeocodes: boolean,
    selectedRows: Array<string>,
    noOfLocationsThatNeedAttention: number,
    handleLocationSelect: Function,
    handleGoToNextItem: Function,
    handleGoToPreviousItem: Function,
    handleSearchTextChange: Function,
    handleClearGroupText: Function,
    handleListEscape: Function,
    handleListEnter: Function,
    handleClearText: Function,
    handleHideSpider: Function,
    handleLatLngClick: Function,
    onApplySystemGeocode: Function,
    onToggleValidateGeocodes: Function,
    onGeocode: Function,
    onDismiss: boolean => void,
    handleTabChange: (currentTab: string) => void,
    handleAddressChange: ({
        locationEntity: GroupedGeocodeEditLocation | GeocodeEditLocation,
        latLng: { lat: string, lng: string }
    }) => void,
    handleGroupVerify: ({ locationEntity: GroupedGeocodeEditLocation }) => void,
    handleVerify: ({ locationEntity: GeocodeEditLocation }) => void,
    handleGroupCardClick: ({
        locationEntity: GroupedGeocodeEditLocation
    }) => void,
    handleCardClick: ({ locationEntity: GeocodeEditLocation }) => void,
    handleResetLocation: ({ locationEntity: GeocodeEditLocation }) => void,
    handleResetGroupLocations: ({
        locationEntity: GroupedGeocodeEditLocation
    }) => void
};
class BulkGeocodeEditView extends React.PureComponent<Props> {
    componentWillUnmount() {
        const { onGeocodeCancel } = this.props;
        onGeocodeCancel && onGeocodeCancel();
    }

    onDismiss = () => {
        const { onDismiss, allLocations } = this.props;
        let changed = false;
        if (
            allLocations.some(
                location => !location.isOriginal || location.isVerifyChanged
            )
        ) {
            changed = true;
        }
        onDismiss && onDismiss(changed);
    };

    onMapCenterAndRadiusUpdate = ({ center, radius }) => {
        this.centerAndRadius = { center, radius };
    };

    getRadiusAndCenter = () => {
        return this.centerAndRadius;
    };

    render() {
        const {
            theme,
            locations,
            expandedLocationId,
            selectedLocationId,
            positionInSortedList,
            groupPositionInList,
            searchText,
            handleLocationSelect,
            handleGoToNextItem,
            handleGoToPreviousItem,
            handleCardClick,
            handleSearchTextChange,
            handleClearGroupText,
            handleResetLocation,
            handleListEscape,
            handleListEnter,
            handleVerify,
            handleClearText,
            handleHideSpider,
            handleLatLngClick,
            onApplySystemGeocode,
            groupedLocations,
            currentTab,
            groupBy,
            handleGroupCardClick,
            handleGroupVerify,
            handleAddressChange,
            handleTabChange,
            handleResetGroupLocations,
            expandedGroupId,
            isBulkGeocodeInProgress,
            isValidatingGeocodes,
            onToggleValidateGeocodes,
            isValidateGeocodesActive,
            noOfLocationsThatNeedAttention,
            handleCardToggle,
            checkedCardIds,
            handleToggleAll,
            listFilters,
            appliedFilters,
            handleFiltersChange,
            onApplySelectedSytemGeocodes,
            isEditable
        } = this.props;
        return (
            <OverlayDialog
                active
                className={theme.overlay}
                dialogClassName={theme.dialog}
            >
                <TabbedBulkAddressList
                    listFilters={listFilters}
                    appliedFilters={appliedFilters}
                    onFilterChange={handleFiltersChange}
                    showLatLng
                    locations={locations}
                    groupedLocations={groupedLocations}
                    groupBy={groupBy}
                    searchText={searchText}
                    selectedLocationId={selectedLocationId}
                    expandedLocationId={expandedLocationId}
                    expandedGroupId={expandedGroupId}
                    positionInSortedList={positionInSortedList}
                    groupPositionInList={groupPositionInList}
                    currentTab={currentTab}
                    isEditable={isEditable}
                    shouldShowOriginalColor
                    handleLocationSelect={handleLocationSelect}
                    handleGoToNextItem={handleGoToNextItem}
                    handleGoToPreviousItem={handleGoToPreviousItem}
                    handleEscape={handleListEscape}
                    handleEnter={handleListEnter}
                    handleCardClick={handleCardClick}
                    handleSearchTextChange={handleSearchTextChange}
                    handleClearGroupText={handleClearGroupText}
                    handleResetLocation={handleResetLocation}
                    handleClearText={handleClearText}
                    handleVerify={handleVerify}
                    handleGroupCardClick={handleGroupCardClick}
                    handleGroupVerify={handleGroupVerify}
                    handleAddressChange={handleAddressChange}
                    handleResetGroupLocations={handleResetGroupLocations}
                    handleTabChange={handleTabChange}
                    handleLatLngClick={handleLatLngClick}
                    onApplySystemGeocode={onApplySystemGeocode}
                    onApplySelectedSytemGeocodes={onApplySelectedSytemGeocodes}
                    getRadiusAndCenter={this.getRadiusAndCenter}
                    isValidateGeocodesActive={isValidateGeocodesActive}
                    handleCardToggle={handleCardToggle}
                    checkedCardIds={checkedCardIds}
                    handleToggleAll={handleToggleAll}
                />
                <BaseGeocoderMap
                    noOfLocationsThatNeedAttention={
                        noOfLocationsThatNeedAttention
                    }
                    locations={locations}
                    selectedLocationId={selectedLocationId}
                    searchText={searchText}
                    shouldShowOriginalColor
                    isEditable={isEditable}
                    handleAddressChange={handleAddressChange}
                    handleLocationSelect={handleLocationSelect}
                    onClose={this.onDismiss}
                    onToggleValidateGeocodes={onToggleValidateGeocodes}
                    isValidateGeocodesActive={isValidateGeocodesActive}
                    handleHideSpider={handleHideSpider}
                    onMapCenterAndRadiusUpdate={this.onMapCenterAndRadiusUpdate}
                    isValidatingGeocodes={isValidatingGeocodes}
                />
                {isBulkGeocodeInProgress ? <Loader active /> : null}
            </OverlayDialog>
        );
    }
}

const ThemedBulkGeocodeEditView = themr(
    'BulkGeocodeEditView',
    defaultTheme
)(BulkGeocodeEditView);

export default ThemedBulkGeocodeEditView;
